/* eslint-env browser */
import merge from 'lodash/merge'
import get from 'lodash/get'
import find from 'lodash/find'
import { ActionType } from 'redux-promise-middleware'
import {
  clearCart,
  addToCart,
  incrementLineItemQuantity,
  decrementLineItemQuantity,
  removeFromCart,
  cartActionCreator,
  syncCartWithShopify,
  updateCartFromShopify
} from '../actions/cart'
import { getCartId, getCartLineItems, isCartCompleted, setCartError } from '../slices/cart'
import {
  syncShopifyCart,
  addItemToShopifyCart,
  removeItemFromShopifyCart,
  updateShopifyCartItemQuantity
} from '../api'

const cartStorageKey = 'discoveredFoodsCart1'

function getLocalStorageObject (key) {
  try {
    const raw = window.localStorage.getItem(key)
    return raw ? JSON.parse(raw) : undefined
  } catch (e) {
    console.error('Error getting from localStorage:', e)
    return undefined
  }
}

function getLocalStorageCart (getState) {
  return getLocalStorageObject(cartStorageKey)
}

function updateLocalStorageCart (getState) {
  const id = getCartId(getState())
  if (!id) {
    return
  }
  const cart = merge(getLocalStorageCart(getState) || {}, { id })
  try {
    window.localStorage.setItem(cartStorageKey, JSON.stringify(cart))
    // **local-storage-fixes-s//
    // Also update the cartId in localStorage for consistency
    window.localStorage.setItem('cartId', id)
    // **local-storage-fixes-e//
  } catch (e) {
    console.error('Error saving to localStorage:', e)
  }
}

function removeLocalStorageCart (getState) {
  try {
    window.localStorage.removeItem(cartStorageKey)
    // **local-storage-fixes-s//
    window.localStorage.removeItem('cartId')
    window.localStorage.removeItem('cartLines')
    // **local-storage-fixes-e//
  } catch (e) {
    console.error('Error removing from localStorage:', e)
  }
}

// **local-storage-fixes-s//
// Add a new function to validate and clean invalid cart data
function validateAndCleanCart () {
  try {
    const cartId = window.localStorage.getItem('cartId')
    const cartLines = window.localStorage.getItem('cartLines')
    const discoveredCart = window.localStorage.getItem(cartStorageKey)
    // If we have a cart ID but no lines, or lines but no cart ID, clear everything
    if ((cartId && !cartLines) || (!cartId && cartLines)) {
      console.log('🧹 Cleaning inconsistent cart data from localStorage')
      window.localStorage.removeItem('cartId')
      window.localStorage.removeItem('cartLines')
      window.localStorage.removeItem(cartStorageKey)
      return false
    }
    // Check if the discovered cart has an ID but localStorage cartId is missing
    if (discoveredCart && !cartId) {
      try {
        const parsed = JSON.parse(discoveredCart)
        if (parsed.id) {
          console.log('🧹 Found cart ID in discovered cart but missing in cartId, cleaning...')
          window.localStorage.removeItem(cartStorageKey)
          return false
        }
      } catch (e) {
        console.log('🧹 Invalid discovered cart data, cleaning...')
        window.localStorage.removeItem(cartStorageKey)
        return false
      }
    }
    return true
  } catch (e) {
    console.error('Error validating cart data:', e)
    // If there's an error, clear everything to be safe
    window.localStorage.removeItem('cartId')
    window.localStorage.removeItem('cartLines')
    window.localStorage.removeItem(cartStorageKey)
    return false
  }
}
// **local-storage-fixes-e//

// Ensure variant IDs are consistent
function normalizeVariantId (variantId) {
  // If it's already a gid URL or base64 encoded, return as is
  if (typeof variantId === 'string' && (variantId.startsWith('gid://') || variantId.startsWith('Z2lk'))) {
    return variantId
  }

  // Otherwise, use the raw ID
  return variantId
}

// Determine if this is the first load of the application
let hasInitialized = false

export default store => next => async action => {
  // Check for initial app load
  if (!hasInitialized && typeof window !== 'undefined') {
    hasInitialized = true
    // **local-storage-fixes-s//
    // Validate cart data before syncing
    validateAndCleanCart()
    // **local-storage-fixes-e//
    // Sync cart on first load
    setTimeout(() => {
      syncShopifyCart(store.dispatch, store.getState)
    }, 500) // Increased timeout for more reliable loading
  }

  // Handle cart synchronization
  if (action.type === syncCartWithShopify.toString()) {
    try {
      await syncShopifyCart(store.dispatch, store.getState)
    } catch (error) {
      console.error('Error syncing cart with Shopify:', error)
    }
  }

  // Handle adding items to cart
  if (action.type === addToCart.toString()) {
    const { variantId, quantity, selling_plan: sellingPlanId } = action.payload
    const normalizedVariantId = normalizeVariantId(variantId)

    try {
      await addItemToShopifyCart({
        dispatch: store.dispatch,
        getState: store.getState,
        variantId: normalizedVariantId,
        quantity: parseInt(quantity, 10),
        sellingPlanId
      })
    } catch (error) {
      console.error(`Error adding item to cart (${normalizedVariantId}):`, error)
      // Try to sync cart after error
      syncShopifyCart(store.dispatch, store.getState)
    }
  }

  // Handle incrementing line item quantity
  // if (action.type === incrementLineItemQuantity.toString()) {
  //   const normalizedVariantId = normalizeVariantId(action.payload)
  //   await alterCartLineItemsQuantity(store.dispatch, store.getState, [
  //     { variantId: normalizedVariantId, numberToAdd: 1 }
  //   ])
  // }

  // // Handle decrementing line item quantity
  // if (action.type === decrementLineItemQuantity.toString()) {
  //   const normalizedVariantId = normalizeVariantId(action.payload)
  //   await alterCartLineItemsQuantity(store.dispatch, store.getState, [
  //     { variantId: normalizedVariantId, numberToAdd: -1 }
  //   ])
  // }

  // Handle incrementing line item quantity
  if (action.type === incrementLineItemQuantity.toString()) {
    const variantId = normalizeVariantId(action.payload)
    const lineItems = getCartLineItems(store.getState())
    const lineItem = find(lineItems,
      item => normalizeVariantId(get(item, ['variant', 'id'])) === variantId
    )
    if (lineItem) {
      const newQty = lineItem.quantity + 1
      updateShopifyCartItemQuantity({
        dispatch: store.dispatch,
        getState: store.getState,
        lineItemId: lineItem.id,
        quantity: newQty
      })
        .then(result => {
          if (!result) {
            setTimeout(
              () => store.dispatch(updateCartFromShopify(store.getState().cart.content)),
              1000
            )
          }
        })
        .catch(error => {
          console.warn('🛑 Error from incrementing:', error?.message)
          store.dispatch(setCartError({
            message: `${variantId} :: ${error?.message || 'Error updating quantity'}`
          }))
          store.dispatch(updateCartFromShopify(store.getState().cart.content))
        })
    }
  }

  // Handle decrementing line item quantity
  if (action.type === decrementLineItemQuantity.toString()) {
    const variantId = normalizeVariantId(action.payload)
    const lineItems = getCartLineItems(store.getState())
    const lineItem = find(lineItems,
      item => normalizeVariantId(get(item, ['variant', 'id'])) === variantId
    )
    if (lineItem) {
      const newQty = lineItem.quantity - 1
      if (newQty <= 0) {
        removeItemFromShopifyCart({
          dispatch: store.dispatch,
          getState: store.getState,
          lineItemId: lineItem.id
        })
          .then(result => {
            if (!result) {
              setTimeout(
                () => store.dispatch(updateCartFromShopify(store.getState().cart.content)),
                1000
              )
            }
          })
          .catch(() => {
            store.dispatch(updateCartFromShopify(store.getState().cart.content))
          })
      } else {
        updateShopifyCartItemQuantity({
          dispatch: store.dispatch,
          getState: store.getState,
          lineItemId: lineItem.id,
          quantity: newQty
        })
          .then(result => {
            if (!result) {
              setTimeout(
                () => store.dispatch(updateCartFromShopify(store.getState().cart.content)),
                1000
              )
            }
          })
          .catch(() => {
            store.dispatch(updateCartFromShopify(store.getState().cart.content))
          })
      }
    }
  }

  // Handle removing items from cart
  if (action.type === removeFromCart.toString()) {
    const lineItemId = action.payload // This is the direct line item ID

    if (lineItemId) {
      try {
        // console.log(`Removing item with line item ID: ${lineItemId}`)

        // Make the API call to remove the item
        removeItemFromShopifyCart({
          dispatch: store.dispatch,
          getState: store.getState,
          lineItemId
        })
          .then(result => {
            // If we didn't get a result, make sure to reset loading state
            if (!result) {
              // Fallback to ensure loading state is reset
              setTimeout(() => {
                store.dispatch(updateCartFromShopify(store.getState().cart.content))
              }, 1000)
            }
          })
          .catch(error => {
            console.error(`Error in remove item flow: ${error}`)
            // Even if there's an error, make sure to reset the loading state
            store.dispatch(updateCartFromShopify(store.getState().cart.content))
          })
      } catch (error) {
        console.error(`Error removing item from cart (${lineItemId}):`, error)
        // Reset loading state if any error occurs
        store.dispatch(updateCartFromShopify(store.getState().cart.content))
      }
    } else {
      // If no line item ID was provided, still reset the loading state
      store.dispatch(updateCartFromShopify(store.getState().cart.content))
    }
  }

  // Pass action to next middleware/reducer
  const ret = next(action)

  // Handle cart completion
  if (action.type === `${cartActionCreator.toString()}_${ActionType.Fulfilled}`) {
    const cartId = getCartId(store.getState())
    if (cartId) {
      const completed = isCartCompleted(store.getState())
      if (!completed) {
        updateLocalStorageCart(store.getState)
      } else {
        removeLocalStorageCart(store.getState)
        store.dispatch(clearCart())
      }
    } else {
      // null returned from GET means the cart does not exist
      removeLocalStorageCart(store.getState)
    }
  }

  return ret
}
